import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { sqlRead } from "../utils/sqlClient";
import { useIsAdmin, AdminGate } from "../auth/AdminContext";
import DrawingStorageCheck from "./DrawingStorageCheck";

// ── These are placeholders until the actual Intuit/Autodesk app registrations
// and the cs-est-qbo-auth / cs-est-acc-auth Logic Apps exist. Fill in once
// the Intuit Developer "Keys and credentials" step is complete.
const QBO_CLIENT_ID      = "REPLACE_WITH_QBO_PRODUCTION_CLIENT_ID";
const QBO_REDIRECT_URI   = "https://estimating.cleanspaceus.com/integrations/qbo-connect";
const QBO_AUTHORIZE_URL  = "https://appcenter.intuit.com/connect/oauth2";
const QBO_SCOPE          = "com.intuit.quickbooks.accounting";

const ENTITIES = ["CleanSpace", "Allied", "CleanShield", "TECHspace"];
const ACC_ELIGIBLE = ["CleanSpace", "Allied", "CleanShield", "TECHspace"];

function StatusPill({ status }) {
  const colors = {
    "Connected":     { bg: "#ecfdf5", fg: "#059669", dot: "#059669" },
    "Not Connected": { bg: "#f3f4f6", fg: "#6b7280", dot: "#9ca3af" },
    "Error":         { bg: "#fef2f2", fg: "#dc2626", dot: "#dc2626" },
  };
  const c = colors[status] || colors["Not Connected"];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.fg,
    }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot }} />
      {status}
    </div>
  );
}

// ── Main dashboard (Launch URL target) ───────────────────────────────────────
function IntegrationsDashboard() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    sqlRead("EXEC sp_GetIntegrationConnections")
      .then(setConnections)
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  function getConn(entity, system) {
    return connections.find(c => c.Entity === entity && c.SystemName === system);
  }

  function startQboConnect(entity) {
    const state = encodeURIComponent(JSON.stringify({ entity, system: "QBO" }));
    const url = `${QBO_AUTHORIZE_URL}?client_id=${QBO_CLIENT_ID}&redirect_uri=${encodeURIComponent(QBO_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(QBO_SCOPE)}&state=${state}`;
    window.location.href = url;
  }

  return (
    <div style={{ maxWidth: 900, margin: "32px auto", padding: "0 24px" }}>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Integrations</div>
      <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 24 }}>
        QuickBooks Online and Autodesk Construction Cloud connections, one per contracting entity.
        These power the automatic job creation triggered by job number assignment (Item 5).
      </div>

      <div style={{
        marginBottom: 20, padding: "12px 16px", background: "#fffbeb",
        border: "1px solid #f59e0b", borderRadius: 8, fontSize: 12, color: "#92400e",
      }}>
        ⚠️ This is a status dashboard scaffold. The actual OAuth token exchange (which requires
        the app's client secret) and the job-creation automation both depend on the
        <code> cs-est-qbo-auth</code> / <code>cs-est-acc-auth</code> Logic Apps, which are not yet built.
        "Connect" below will redirect to Intuit's OAuth screen but the callback page cannot
        complete the handshake until that Logic App exists.
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>QuickBooks Online</div>
      <div style={{ border: "1px solid var(--cs-border)", borderRadius: 10, overflow: "hidden", marginBottom: 28 }}>
        {ENTITIES.map((entity, i) => {
          const conn = getConn(entity, "QBO");
          return (
            <div key={entity} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 16px",
              borderTop: i > 0 ? "1px solid var(--cs-border)" : "none",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{entity}</div>
                {conn?.RealmID && <div style={{ fontSize: 11, color: "var(--text3)" }}>Realm ID: {conn.RealmID}</div>}
                {conn?.ConnectedAt && <div style={{ fontSize: 11, color: "var(--text3)" }}>Connected {new Date(conn.ConnectedAt).toLocaleDateString()}</div>}
                {conn?.LastError && <div style={{ fontSize: 11, color: "#dc2626" }}>{conn.LastError}</div>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <StatusPill status={loading ? "Not Connected" : (conn?.Status || "Not Connected")} />
                <button
                  onClick={() => startQboConnect(entity)}
                  style={{
                    padding: "6px 14px", fontSize: 12, fontWeight: 600,
                    background: conn?.Status === "Connected" ? "none" : "#0ea5e9",
                    color: conn?.Status === "Connected" ? "var(--text2)" : "#fff",
                    border: conn?.Status === "Connected" ? "1px solid var(--cs-border)" : "none",
                    borderRadius: 6, cursor: "pointer",
                  }}
                >
                  {conn?.Status === "Connected" ? "Reconnect" : "Connect"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Autodesk Construction Cloud</div>
      <div style={{ border: "1px solid var(--cs-border)", borderRadius: 10, overflow: "hidden" }}>
        {ACC_ELIGIBLE.map((entity, i) => {
          const conn = getConn(entity, "ACC");
          return (
            <div key={entity} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 16px",
              borderTop: i > 0 ? "1px solid var(--cs-border)" : "none",
            }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{entity}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <StatusPill status={loading ? "Not Connected" : (conn?.Status || "Not Connected")} />
                <button
                  disabled
                  title="Autodesk app registration not yet built"
                  style={{
                    padding: "6px 14px", fontSize: 12, fontWeight: 600,
                    background: "none", color: "var(--text3)",
                    border: "1px solid var(--cs-border)", borderRadius: 6,
                    cursor: "not-allowed", opacity: 0.6,
                  }}
                >
                  Connect
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>
        Manufacturing (CleanSpace Systems) has no ACC account and is excluded here — it only ever receives a QBO job entry as a subcontracted entity.
      </div>

      <DrawingStorageCheck />
    </div>
  );
}

// ── OAuth callback target (Connect/Reconnect URL) ────────────────────────────
function QboConnectCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const code = searchParams.get("code");
    const realmId = searchParams.get("realmId");
    const stateRaw = searchParams.get("state");

    if (!code) {
      // No code present — this is the bare Connect/Reconnect URL being hit
      // directly (e.g. from Intuit's app dashboard) rather than after an
      // OAuth redirect. Send the user back to the dashboard to pick an entity.
      navigate("/integrations", { replace: true });
      return;
    }

    let entity = "Unknown";
    try { entity = JSON.parse(decodeURIComponent(stateRaw))?.entity || "Unknown"; } catch {}

    // TODO: Once cs-est-qbo-auth Logic App exists, POST { code, realmId, entity }
    // to it here. The Logic App exchanges the code for access/refresh tokens
    // using the client secret (never exposed to the browser) and calls
    // sp_UpsertIntegrationConnection to record the result. For now this just
    // reports that the piece isn't wired up yet.
    setStatus("error");
    setMessage(`Received authorization for ${entity} (realm ${realmId}), but the token exchange Logic App isn't built yet — nothing was saved.`);
  }, [searchParams, navigate]);

  return (
    <div style={{ maxWidth: 500, margin: "80px auto", padding: 24, textAlign: "center" }}>
      {status === "processing" && <div style={{ fontSize: 14 }}>Connecting…</div>}
      {status === "error" && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#dc2626", marginBottom: 8 }}>Connection incomplete</div>
          <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>{message}</div>
        </>
      )}
      {status === "success" && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#059669", marginBottom: 8 }}>Connected</div>
          <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>{message}</div>
        </>
      )}
      <button
        onClick={() => navigate("/integrations")}
        style={{
          padding: "8px 18px", fontSize: 13, fontWeight: 600,
          background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
        }}
      >
        Back to Integrations
      </button>
    </div>
  );
}

// ── Disconnect confirmation target (Disconnect URL) ──────────────────────────
function QboDisconnected() {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 500, margin: "80px auto", padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Disconnected</div>
      <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
        The QuickBooks Online connection has been disconnected.
      </div>
      <button
        onClick={() => navigate("/integrations")}
        style={{
          padding: "8px 18px", fontSize: 13, fontWeight: 600,
          background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
        }}
      >
        Back to Integrations
      </button>
    </div>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────
export default function Integrations() {
  const location = useLocation();
  // Admin-only (SWA-ESTIMATING-ADMIN) — this page connects/disconnects the
  // company's QBO/ACC integrations. It was reachable by URL without a gate
  // until 2026-09-11; the OAuth callback routes are gated too, since only an
  // admin should ever be completing a connection.
  const isAdmin = useIsAdmin();
  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  if (location.pathname.endsWith("/qbo-connect")) return <QboConnectCallback />;
  if (location.pathname.endsWith("/disconnected")) return <QboDisconnected />;
  return <IntegrationsDashboard />;
}
